import { useState, useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { runLivenessChallenge, pickInstruction } from "../lib/liveness";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { User, Camera, MapPin, ShieldCheck, Check, RefreshCw, Loader2, IdCard, Upload } from "lucide-react";
import { useAuth } from "../context/AuthContext";

// Onboarding Phase 1 — profil obligatoire avec SELFIE EN DIRECT (caméra uniquement, pas de galerie)
// pour garantir l'identité de l'utilisateur avant d'accéder à la carte.
export function ProfileSetup({ onDone, onClose }) {
  const { user } = useAuth();
  const requireId = user?.role === "passenger"; // CNI + vérif IA obligatoire pour les passagers
  const idInputRef = useRef(null);
  const [idPreview, setIdPreview] = useState("");
  const [idUploaded, setIdUploaded] = useState(false);
  const [idUploading, setIdUploading] = useState(false);
  const [idVerifying, setIdVerifying] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [starting, setStarting] = useState(false);
  const [instruction, setInstruction] = useState(null);
  const [checking, setChecking] = useState(false);
  const [camError, setCamError] = useState("");
  const [preview, setPreview] = useState(""); // captured selfie preview (data URL)
  const [photoUploaded, setPhotoUploaded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [city, setCity] = useState("");
  const [phone, setPhone] = useState(user?.phone || "");
  const [momoNumber, setMomoNumber] = useState("");
  const [momoOperator, setMomoOperator] = useState("orange");
  const [isEurope, setIsEurope] = useState(() => {
    try { return localStorage.getItem("bsg_zone") === "europe" || user?.zone === "europe"; } catch { return user?.zone === "europe"; }
  });
  const [saving, setSaving] = useState(false);

  // Mobile Money (Orange/MTN) réservé à l'Afrique : on masque le bloc pour les utilisateurs Europe.
  // On privilégie la zone déjà mémorisée (affinée par GPS dans le dashboard) ; détection IP en secours seulement.
  useEffect(() => {
    let stored = null;
    try { stored = localStorage.getItem("bsg_zone"); } catch { /* ignore */ }
    if (stored || user?.zone) return;
    let active = true;
    api.get("/region/detect").then(({ data }) => { if (active && data?.zone) setIsEurope(data.zone === "europe"); }).catch(() => {});
    return () => { active = false; };
  }, []); // eslint-disable-line

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraOn(false);
  }, []);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const startCamera = async () => {
    setCamError("");
    setStarting(true);
    if (!navigator.mediaDevices?.getUserMedia) {
      setStarting(false);
      setCamError("Votre appareil/navigateur ne permet pas l'accès à la caméra.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      setCameraOn(true);
      setInstruction(pickInstruction());
      setPreview("");
      setPhotoUploaded(false);
      // attach after render
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      }, 50);
    } catch (err) {
      const name = err?.name || "";
      if (name === "NotAllowedError" || name === "SecurityError")
        setCamError("Accès caméra refusé. Autorisez la caméra dans votre navigateur pour prendre un selfie.");
      else if (name === "NotFoundError" || name === "OverconstrainedError")
        setCamError("Aucune caméra détectée sur cet appareil.");
      else setCamError("Impossible d'ouvrir la caméra. Réessayez.");
    } finally {
      setStarting(false);
    }
  };

  const analyzeSharpness = (canvas) => {
    // Variance d'un Laplacien approx sur une version réduite en niveaux de gris → détecte les photos floues.
    const s = 128;
    const tmp = document.createElement("canvas");
    tmp.width = s; tmp.height = s;
    const c = tmp.getContext("2d");
    c.drawImage(canvas, 0, 0, s, s);
    const { data } = c.getImageData(0, 0, s, s);
    const gray = new Float32Array(s * s);
    for (let i = 0; i < s * s; i++) gray[i] = 0.299 * data[i * 4] + 0.587 * data[i * 4 + 1] + 0.114 * data[i * 4 + 2];
    let mean = 0; const lap = new Float32Array(s * s);
    for (let y = 1; y < s - 1; y++) for (let x = 1; x < s - 1; x++) {
      const idx = y * s + x;
      const v = 4 * gray[idx] - gray[idx - 1] - gray[idx + 1] - gray[idx - s] - gray[idx + s];
      lap[idx] = v; mean += v;
    }
    mean /= s * s;
    let variance = 0;
    for (let i = 0; i < s * s; i++) variance += (lap[i] - mean) ** 2;
    return variance / (s * s);
  };

  const detectFace = async (canvas) => {
    // Détection de visage si le navigateur la supporte (Chrome/Android). Sinon on ne bloque pas.
    try {
      if ("FaceDetector" in window) {
        const fd = new window.FaceDetector({ fastMode: true, maxDetectedFaces: 1 });
        const faces = await fd.detect(canvas);
        return faces.length > 0;
      }
    } catch { /* ignore, non bloquant */ }
    return null; // indéterminé
  };

  const captureSelfie = async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return toast.error("La caméra n'est pas prête, patientez une seconde.");
    const size = Math.min(video.videoWidth, video.videoHeight);
    const canvas = canvasRef.current;
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    const sx = (video.videoWidth - size) / 2;
    const sy = (video.videoHeight - size) / 2;
    ctx.drawImage(video, sx, sy, size, size, 0, 0, size, size);

    // Anti-fraude : netteté + vivacité renforcée (instruction aléatoire) + présence d'un visage.
    const sharpness = analyzeSharpness(canvas);
    if (sharpness < 4) return toast.error("Photo trop floue. Tenez l'appareil stable et reprenez le selfie.");
    setChecking(true);
    const live = await runLivenessChallenge(video, instruction?.id);
    setChecking(false);
    if (!live.ok) return toast.error(live.reason);
    const hasFace = await detectFace(canvas);
    if (hasFace === false) return toast.error("Aucun visage détecté. Cadrez bien votre visage dans le cercle.");

    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    setPreview(dataUrl);
    stopCamera();
    setUploading(true);
    setPhotoUploaded(false);
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const fd = new FormData();
      fd.append("file", blob, "selfie.jpg");
      await api.post("/auth/profile/photo", fd);
      setPhotoUploaded(true);
      toast.success("Selfie enregistré ✓");
    } catch (err) {
      setPreview("");
      toast.error(apiError(err));
    } finally {
      setUploading(false);
    }
  };

  const pickIdCard = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("La pièce d'identité doit être une image (JPG/PNG).");
    if (f.size > 8 * 1024 * 1024) return toast.error("Image trop lourde (max 8 Mo).");
    setIdPreview(URL.createObjectURL(f));
    setIdUploaded(false);
    setIdUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f, f.name || "id-card.jpg");
      const { data } = await api.post("/auth/profile/id-card", fd);
      setIdUploaded(true);
      if (data?.verifying) {
        setIdVerifying(true);
        toast.success("Pièce d'identité envoyée ✓ — vérification en cours");
        setTimeout(() => setIdVerifying(false), 6000);
      } else {
        toast.success("Pièce d'identité envoyée ✓");
      }
    } catch (err) {
      setIdPreview("");
      toast.error(apiError(err));
    } finally {
      setIdUploading(false);
    }
  };

  const submit = async () => {
    if (!isEurope && !photoUploaded) return toast.error("Prenez d'abord un selfie en direct.");
    if (!isEurope && requireId && !idUploaded) return toast.error("Ajoutez d'abord une photo de votre pièce d'identité.");
    if (!firstName.trim() || !lastName.trim()) return toast.error("Nom et prénom obligatoires.");
    if (!isEurope && !city.trim()) return toast.error("Ville obligatoire.");
    if (!phone.trim()) return toast.error("Numéro de téléphone obligatoire.");
    setSaving(true);
    try {
      await api.put("/auth/profile/complete", { first_name: firstName.trim(), last_name: lastName.trim(), city: city.trim(), phone: phone.trim(), momo_number: momoNumber.trim(), momo_operator: momoOperator, zone: isEurope ? "europe" : "africa" });
      toast.success("Profil complété ✓");
      onDone && (await onDone());
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="profile-setup" className="min-h-screen bg-[#FDF8F0] flex flex-col">
      <canvas ref={canvasRef} className="hidden" />
      <div className="max-w-md w-full mx-auto px-5 pt-8 pb-44 flex-1 flex flex-col">
        {onClose && (
          <button data-testid="profile-setup-back" onClick={onClose} className="self-start inline-flex items-center gap-2 text-sm font-semibold text-[#75807A] hover:text-[#1A1A1A] transition-colors">
            <RefreshCw className="w-4 h-4 rotate-180" /> Retour
          </button>
        )}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}
          className="mt-6 bg-white rounded-3xl border border-[#E2DFD8] p-7 shadow-[0_8px_40px_rgba(0,0,0,0.05)]"
        >
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-[#D94F2B]">
            <User className="w-7 h-7 text-white" />
          </div>
          <h1 className="mt-5 font-head font-black text-2xl tracking-tight text-[#1A1A1A]">Complétez votre profil</h1>
          <p className="mt-1 text-[#75807A] text-sm">{isEurope ? "Renseignez vos informations pour commander en quelques secondes — aucun selfie requis, votre vie privée est respectée." : (<>Pour garantir votre identité, un <b>selfie en direct</b> est obligatoire (photo prise à l'instant, pas depuis la galerie).</>)}</p>

          {!isEurope && (<>
          {/* Selfie caméra */}
          <div className="mt-6 flex flex-col items-center">
            <div className="relative w-40 h-40 rounded-full overflow-hidden border-2 border-[#E2DFD8] bg-[#0F0F0F] flex items-center justify-center">
              {preview ? (
                <img src={preview} alt="selfie" data-testid="selfie-preview" className="w-full h-full object-cover" />
              ) : cameraOn ? (
                <video ref={videoRef} data-testid="selfie-video" autoPlay playsInline muted className="w-full h-full object-cover -scale-x-100" />
              ) : (
                <Camera className="w-9 h-9 text-white/50" />
              )}
              {cameraOn && !preview && (
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <div className="w-28 h-32 rounded-[50%] border-2 border-dashed border-white/80" />
                  <span className="absolute bottom-2 text-[10px] font-semibold text-white bg-black/40 px-2 py-0.5 rounded-full">Placez votre visage ici</span>
                </div>
              )}
              {photoUploaded && (
                <span className="absolute bottom-2 right-2 w-7 h-7 rounded-full bg-[#16a34a] flex items-center justify-center border-2 border-white">
                  <Check className="w-4 h-4 text-white" />
                </span>
              )}
            </div>

            <div className="mt-4 w-full flex flex-col items-center gap-2">
              {cameraOn && instruction && (
                <div data-testid="liveness-instruction" className="w-full rounded-xl bg-[#1A1A1A] text-white text-sm font-bold px-4 py-2.5 text-center">
                  {instruction.label}
                </div>
              )}
              {!cameraOn && !preview && (
                <Button data-testid="open-camera-btn" onClick={startCamera} disabled={starting} className="h-12 px-6 rounded-xl bg-[#1A1A1A] hover:bg-black text-white font-bold inline-flex items-center gap-2">
                  {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} Ouvrir la caméra
                </Button>
              )}
              {cameraOn && (
                <Button data-testid="capture-selfie-btn" onClick={captureSelfie} disabled={checking} className="h-12 px-6 rounded-xl bg-[#D94F2B] hover:-translate-y-0.5 transition-transform text-white font-bold inline-flex items-center gap-2">
                  {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} {checking ? "Vérification…" : "Prendre le selfie"}
                </Button>
              )}
              {preview && (
                <button data-testid="retake-selfie-btn" onClick={startCamera} disabled={uploading} className="text-sm font-semibold text-[#D94F2B] inline-flex items-center gap-1.5">
                  <RefreshCw className="w-3.5 h-3.5" /> Reprendre le selfie
                </button>
              )}
              <p className="text-xs text-[#75807A]">{uploading ? "Enregistrement du selfie…" : preview ? (photoUploaded ? "Selfie vérifié ✓" : "") : "Selfie en direct (obligatoire)"}</p>
              {camError && <p data-testid="camera-error" className="text-xs text-[#D32F2F] text-center">{camError}</p>}
            </div>
          </div>
          </>)}

          {/* Pièce d'identité (CNI/Passeport) — passagers uniquement, comparée au selfie par IA */}
          {!isEurope && requireId && (
            <div className="mt-6">
              <label className="text-sm font-medium text-[#3B423E]">Pièce d'identité (CNI ou passeport)</label>
              <input ref={idInputRef} data-testid="id-card-input" type="file" accept="image/*" capture="environment" onChange={pickIdCard} className="hidden" />
              <div className="mt-1.5 rounded-2xl border-2 border-dashed border-[#E2DFD8] p-4 flex items-center gap-4">
                <div className="w-20 h-14 rounded-lg overflow-hidden bg-[#F4F1EA] flex items-center justify-center shrink-0">
                  {idPreview ? (
                    <img src={idPreview} alt="pièce d'identité" data-testid="id-card-preview" className="w-full h-full object-cover" />
                  ) : (
                    <IdCard className="w-7 h-7 text-[#B7B2A8]" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <Button data-testid="upload-id-card-btn" onClick={() => idInputRef.current?.click()} disabled={idUploading} className="h-10 px-4 rounded-xl bg-[#1A1A1A] hover:bg-black text-white font-semibold text-sm inline-flex items-center gap-2">
                    {idUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {idUploaded ? "Remplacer la pièce" : "Ajouter ma pièce"}
                  </Button>
                  <p className="mt-1.5 text-xs text-[#75807A]">
                    {idUploading ? "Envoi…" : idVerifying ? "Vérification IA en cours…" : idUploaded ? "Pièce enregistrée ✓" : "Photo nette du recto de votre pièce."}
                  </p>
                </div>
                {idUploaded && !idVerifying && (
                  <span className="w-7 h-7 rounded-full bg-[#16a34a] flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4 text-white" />
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="mt-6 space-y-4">
            <div>
              <label className="text-sm font-medium text-[#3B423E]">Prénom</label>
              <Input data-testid="profile-firstname" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Ex : Jean" className="mt-1.5 h-14 rounded-xl border-2" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#3B423E]">Nom</label>
              <Input data-testid="profile-lastname" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Ex : Mba" className="mt-1.5 h-14 rounded-xl border-2" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#3B423E]">Ville</label>
              <div className="relative mt-1.5">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#75807A]" />
                <Input data-testid="profile-city" value={city} onChange={(e) => setCity(e.target.value)} placeholder={isEurope ? "Ex : Bruxelles" : "Ex : Douala"} className="h-14 rounded-xl border-2 pl-11" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[#3B423E]">Numéro de téléphone</label>
              <div className="relative mt-1.5">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#75807A]" />
                <Input data-testid="profile-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={isEurope ? "Ex : +32 4XX XX XX XX" : "Ex : +237 6XX XXX XXX"} className="h-14 rounded-xl border-2 pl-11" />
              </div>
            </div>
            {!isEurope && (
            <div className="rounded-2xl border-2 border-[#E2DFD8] p-4">
              <label className="text-sm font-semibold text-[#3B423E]">Mobile Money <span className="font-normal text-[#75807A]">· Afrique (facultatif)</span></label>
              <p className="text-xs text-[#75807A] mt-0.5 mb-2">Pour vos recharges et paiements Orange Money / MTN MoMo.</p>
              <div className="flex gap-2 mb-2">
                <button type="button" data-testid="profile-momo-orange" onClick={() => setMomoOperator("orange")} className={`flex-1 h-11 rounded-xl border-2 font-semibold text-sm ${momoOperator === "orange" ? "border-[#F26419] bg-[#F26419]/10 text-[#D94F2B]" : "border-[#E2DFD8] text-[#75807A]"}`}>Orange Money</button>
                <button type="button" data-testid="profile-momo-mtn" onClick={() => setMomoOperator("mtn")} className={`flex-1 h-11 rounded-xl border-2 font-semibold text-sm ${momoOperator === "mtn" ? "border-[#F26419] bg-[#F26419]/10 text-[#D94F2B]" : "border-[#E2DFD8] text-[#75807A]"}`}>MTN MoMo</button>
              </div>
              <Input data-testid="profile-momo-number" type="tel" value={momoNumber} onChange={(e) => setMomoNumber(e.target.value)} placeholder="Numéro Mobile Money" className="h-12 rounded-xl border-2" />
            </div>
            )}
            <Button data-testid="profile-submit" onClick={submit} disabled={saving || uploading || idUploading} className="w-full h-14 rounded-xl text-white font-bold text-lg bg-[#D94F2B] hover:-translate-y-0.5 transition-transform">
              {saving ? "Enregistrement…" : "Continuer"}
            </Button>
          </div>

          <div className="mt-6 flex items-center gap-2 text-xs text-[#75807A]">
            <ShieldCheck className="w-4 h-4 text-[#D94F2B]" /> {isEurope ? "Vos données servent uniquement à assurer votre course. Aucun selfie n'est demandé (RGPD)." : "Votre selfie sert uniquement à vérifier votre identité."}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default ProfileSetup;
